import { useEffect, useRef, useState } from "react";
import { Music } from "lucide-react";

// Generative Indian classical ambience — tanpura-style drone + bansuri-style melody.
// A random raga is chosen each visit (Bhupali / Yaman / Bhairavi). No audio files, no licensing issues.
// Browsers block autoplay-with-sound, so playback begins on the visitor's first interaction.
const RAGAS = [
  { name: "Bhupali", degrees: [0, 2, 4, 7, 9] },
  { name: "Yaman", degrees: [0, 2, 4, 6, 7, 9, 11] },
  { name: "Bhairavi", degrees: [0, 1, 3, 5, 7, 8, 10] },
];
const ROOT = 146.83; // D3
const semitone = (n) => ROOT * Math.pow(2, n / 12);

function makeImpulse(ctx, seconds = 2.4) {
  const len = ctx.sampleRate * seconds;
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
  }
  return buf;
}

class AmbientEngine {
  constructor() {
    this.ctx = null;
    this.timers = [];
  }

  start() {
    if (this.ctx) {
      this.ctx.resume();
      this.master.gain.cancelScheduledValues(this.ctx.currentTime);
      this.master.gain.linearRampToValueAtTime(0.16, this.ctx.currentTime + 2.5);
      return;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.ctx = ctx;
    this.raga = RAGAS[Math.floor(Math.random() * RAGAS.length)];

    this.master = ctx.createGain();
    this.master.gain.value = 0;
    const reverb = ctx.createConvolver();
    reverb.buffer = makeImpulse(ctx);
    const wet = ctx.createGain();
    wet.gain.value = 0.5;
    this.master.connect(ctx.destination);
    this.master.connect(reverb);
    reverb.connect(wet);
    wet.connect(ctx.destination);
    this.master.gain.linearRampToValueAtTime(0.16, ctx.currentTime + 2.5);

    // Tanpura drone — cyclic plucks: Pa – Sa – Sa – Sa
    const strings = [semitone(7), semitone(0), semitone(0), semitone(-12)];
    let step = 0;
    const pluck = () => {
      const f = strings[step % strings.length];
      step += 1;
      const osc = ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.value = f * (1 + (Math.random() - 0.5) * 0.002);
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 900;
      const g = ctx.createGain();
      const t = ctx.currentTime;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.05, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
      osc.connect(lp); lp.connect(g); g.connect(this.master);
      osc.start(t); osc.stop(t + 2);
    };
    pluck();
    this.timers.push(setInterval(pluck, 1500));

    // Bansuri-style melody — gentle random walk through the raga
    let degree = Math.floor(this.raga.degrees.length / 2);
    const note = () => {
      if (Math.random() > 0.18) { // occasional breath rest
        degree = Math.max(0, Math.min(this.raga.degrees.length * 2 - 1,
          degree + [-2, -1, -1, 0, 1, 1, 2][Math.floor(Math.random() * 7)]));
        const deg = this.raga.degrees[degree % this.raga.degrees.length] + 12 * (1 + Math.floor(degree / this.raga.degrees.length));
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = semitone(deg);
        const vib = ctx.createOscillator();
        vib.frequency.value = 4.5 + Math.random();
        const vibGain = ctx.createGain();
        vibGain.gain.value = 4;
        vib.connect(vibGain); vibGain.connect(osc.frequency);
        const g = ctx.createGain();
        const t = ctx.currentTime;
        const dur = 1.6 + Math.random() * 2.2;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.055 + Math.random() * 0.02, t + 0.35);
        g.gain.setValueAtTime(0.05, t + dur - 0.5);
        g.gain.linearRampToValueAtTime(0, t + dur);
        osc.connect(g); g.connect(this.master);
        osc.start(t); osc.stop(t + dur + 0.1);
        vib.start(t); vib.stop(t + dur + 0.1);
        this.timers.push(setTimeout(note, (dur + Math.random() * 1.6) * 1000));
      } else {
        this.timers.push(setTimeout(note, 2000 + Math.random() * 2000));
      }
    };
    this.timers.push(setTimeout(note, 2500));
  }

  stop() {
    if (!this.ctx) return;
    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 1.2);
    const ctx = this.ctx;
    setTimeout(() => ctx.suspend(), 1300);
  }
}

export default function AmbientMusic() {
  const engineRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hint, setHint] = useState(false);

  useEffect(() => {
    engineRef.current = new AmbientEngine();
    const disabled = localStorage.getItem("rmr_music") === "off";
    if (disabled) return;
    setHint(true);
    const begin = () => {
      engineRef.current.start();
      setPlaying(true);
      setHint(false);
      window.removeEventListener("pointerdown", begin);
      window.removeEventListener("keydown", begin);
    };
    window.addEventListener("pointerdown", begin);
    window.addEventListener("keydown", begin);
    return () => {
      window.removeEventListener("pointerdown", begin);
      window.removeEventListener("keydown", begin);
    };
  }, []);

  const toggle = () => {
    if (playing) {
      engineRef.current.stop();
      localStorage.setItem("rmr_music", "off");
      setPlaying(false);
    } else {
      localStorage.setItem("rmr_music", "on");
      engineRef.current.start();
      setPlaying(true);
      setHint(false);
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid="music-toggle"
      aria-label={playing ? "Pause background music" : "Play background music"}
      title={playing ? "Pause music" : "Play music — Indian instrumental"}
      className={`fixed bottom-24 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full border bg-ink/90 backdrop-blur transition-all duration-300 hover:border-gold md:bottom-8 md:right-8 ${
        playing ? "border-gold" : "border-gold/40"
      } ${hint ? "animate-pulse" : ""}`}
    >
      {playing ? (
        <span className="flex items-end gap-[3px]" data-testid="music-playing-bars">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-[3px] animate-pulse rounded-full bg-gold"
              style={{ height: `${10 + i * 4}px`, animationDelay: `${i * 0.18}s`, animationDuration: "0.9s" }}
            />
          ))}
        </span>
      ) : (
        <Music className="h-5 w-5 text-gold" />
      )}
    </button>
  );
}
